// Reauth banner.
//
// The chat surface emits a reauth_required event when the user's GitLab
// refresh token is rejected. We render this banner; clicking the button
// opens the OAuth start URL in a popup so the in-flight workflow state
// survives. The popup signals completion by posting a message back to the
// opener; the onComplete callback re-attaches the WebSocket.

import { useEffect, useState } from "react";

import { useT } from "../i18n/index.js";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";

import { invalidateMe } from "./useAuth";

interface ReauthBannerProps {
  readonly visible: boolean;
  readonly gitlabHost: string;
  readonly onComplete: () => void;
}

const POPUP_MESSAGE = "loom.reauth_complete" as const;

export function ReauthBanner({ visible, gitlabHost, onComplete }: ReauthBannerProps) {
  const t = useT();
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (!popupOpen) return;
    const handler = (ev: MessageEvent) => {
      if (ev.data === POPUP_MESSAGE) {
        setPopupOpen(false);
        // The popup signed in again, maybe as another GitLab account: no guard
        // may reuse the `/auth/me` answer from before it.
        invalidateMe();
        onComplete();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [popupOpen, onComplete]);

  if (!visible) return null;

  return (
    <Banner
      tone="warning"
      action={
        <Button
          variant="secondary"
          onClick={() => {
            const url = `/auth/gitlab/start?return_to=${encodeURIComponent("/__reauth_complete")}`;
            const popup = window.open(url, "loom-reauth", "width=520,height=720");
            if (!popup) {
              // Popup blocker — fall back to same-tab.
              window.location.href = url;
              return;
            }
            setPopupOpen(true);
          }}
        >
          {t("chatTop.reauthSignIn", { host: gitlabHost })}
        </Button>
      }
    >
      {t("chatTop.reauthExpired")}
    </Banner>
  );
}

export const reauthPopupMessage = POPUP_MESSAGE;
